import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
} from 'react';
import {
  hasAuthParams,
  AuthProvider as OidcAuthProvider,
  useAuth as useOidcAuth,
} from 'react-oidc-context';
import { resetDefaultProfile } from '../api/user-logout';
import { clearUsersData } from '../utils';
import { getUserManager } from '../utils/auth-configs';

export type AuthContextValue = {
  isAuthenticated: boolean;
  isTokenInitialized: boolean;
  logout: () => void;
  login: () => void;
};

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

type AuthProviderProps = {
  children: ReactNode;
  mockProvider?: boolean;
};

function onSigninCallback() {
  window.history.replaceState({}, document.title, window.location.pathname);
}

function isOidcCallback() {
  return hasAuthParams() && window.location.pathname === '/';
}

type AuthLike = Pick<
  ReturnType<typeof useOidcAuth>,
  'isAuthenticated' | 'isLoading' | 'signinRedirect' | 'signoutRedirect'
>;

const MOCK_OIDC: AuthLike = {
  isAuthenticated: true,
  isLoading: false,
  signinRedirect: () => Promise.resolve(),
  signoutRedirect: () => Promise.resolve(),
};

function LoginRequiredGuard({ children }: { children: ReactNode }) {
  const oidc = useOidcAuth();

  useEffect(() => {
    if (oidc.isLoading || oidc.isAuthenticated) return;

    if (oidc.error || isOidcCallback()) {
      window.history.replaceState({}, document.title, window.location.pathname);
    }
    getUserManager().signinRedirect();
  }, [oidc.isLoading, oidc.isAuthenticated, oidc.error]);

  return <InnerAuthProvider oidc={oidc}>{children}</InnerAuthProvider>;
}

function InnerAuthProvider({
  children,
  oidc,
}: {
  children: ReactNode;
  oidc: AuthLike;
}) {
  const logout = useCallback(async () => {
    try {
      await resetDefaultProfile();
    } finally {
      clearUsersData();
      await oidc.signoutRedirect({
        post_logout_redirect_uri: window.location.origin,
      });
    }
  }, [oidc.signoutRedirect]);

  const login = useCallback(() => {
    oidc.signinRedirect();
  }, [oidc.signinRedirect]);

  const value = useMemo(
    () => ({
      isAuthenticated: oidc.isAuthenticated,
      isTokenInitialized: !oidc.isLoading && oidc.isAuthenticated,
      login,
      logout,
    }),
    [oidc.isAuthenticated, oidc.isLoading, login, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

function AuthProvider({ children, mockProvider }: AuthProviderProps) {
  if (mockProvider) {
    return <InnerAuthProvider oidc={MOCK_OIDC}>{children}</InnerAuthProvider>;
  }

  return (
    <OidcAuthProvider
      userManager={getUserManager()}
      onSigninCallback={onSigninCallback}
      skipSigninCallback={window.location.pathname !== '/'}
    >
      <LoginRequiredGuard>{children}</LoginRequiredGuard>
    </OidcAuthProvider>
  );
}

function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within a AuthContext');
  }
  return context;
}

export { AuthContext, AuthProvider, useAuth };
